import { useContext, useEffect, useRef } from 'react';
import { useDebounce } from 'use-debounce';
import { ApiContext } from '@api/api';
import { getTimezoneForCountryCode3 } from '@utils/countryTimezone';

export interface ResolveTimezoneInput {
  countryCode3: string | null | undefined;
  city?: string | null;
  postalCode?: string | null;
}

const TEXT_INPUT_DEBOUNCE_MS = 1000;

export const useApplyResolvedTimezone = (
  { countryCode3, city, postalCode }: ResolveTimezoneInput,
  onResolved: (timezoneId: string) => void,
) => {
  const { lookupTimezone } = useContext(ApiContext);
  const isFirstRun = useRef(true);
  const onResolvedRef = useRef(onResolved);
  const lookupTimezoneRef = useRef(lookupTimezone);
  onResolvedRef.current = onResolved;
  lookupTimezoneRef.current = lookupTimezone;

  // City and postal code come from text inputs — debounce so we don't fire a request on every keystroke.
  // Country comes from a dropdown, so it's used as-is.
  const [debouncedCity] = useDebounce(city, TEXT_INPUT_DEBOUNCE_MS);
  const [debouncedPostalCode] = useDebounce(postalCode, TEXT_INPUT_DEBOUNCE_MS);

  useEffect(() => {
    // Skip mount so existing saved timezone (when editing) isn't overwritten before user changes anything.
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (!countryCode3) return;

    let cancelled = false;
    const apply = (tz: string | undefined | null) => {
      if (cancelled || !tz) return;
      onResolvedRef.current(tz);
    };

    const trimmedCity = debouncedCity?.trim();
    if (trimmedCity) {
      lookupTimezoneRef.current(trimmedCity, countryCode3, debouncedPostalCode?.trim() || '', false, (data: { timezoneId?: string } | null) => {
        apply(data?.timezoneId || getTimezoneForCountryCode3(countryCode3));
      });
    } else {
      apply(getTimezoneForCountryCode3(countryCode3));
    }
    return () => {
      cancelled = true;
    };
  }, [countryCode3, debouncedCity, debouncedPostalCode]);
};